import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Calendar, Globe, Wifi, Mail, Phone, ExternalLink, Copy, Check,
  Pencil, Eye, EyeOff, XCircle, Users, Inbox, Clock, StickyNote, Share2, Trash2, Plus, ClipboardList, BarChart3,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api, { fileUrl, formatApiErrorDetail } from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import { StatusBadge } from "../components/StatusBadge";
import { InitiativeEditor } from "../components/InitiativeEditor";
import { localize, countryLabel, languageLabel, professionCategoryLabel, specialtyLabel, PROFESSIONS, ENGAGEMENT_TYPES, COMPENSATION_TYPES, formatDateRange } from "../lib/meta";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

function ParticipantCard({ p, t, showActions, onStatus }) {
  const { lang } = useLang();
  const photo = fileUrl(p.psychologist?.photo_path);
  const ps = p.psychologist || {};
  const formatLabel = ps.service_format === "online" ? t("common.online") : ps.service_format === "in_person" ? t("common.in_person") : t("common.both");
  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid={`participant-${p.id}`}>
      <div className="flex items-start gap-4">
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-secondary">
          {photo ? <img src={photo} alt="" className="h-full w-full object-cover" />
            : <div className="flex h-full w-full items-center justify-center font-display text-lg font-bold text-slateink/30">{(ps.full_name || "?").charAt(0)}</div>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate font-display font-semibold text-slateink">{ps.full_name || "—"}</p>
              {ps.title && <p className="truncate text-sm text-muted-foreground">{ps.title}</p>}
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {ps.profession && <span className="rounded-full bg-slateink px-2 py-0.5 text-[11px] font-semibold text-white">{localize(PROFESSIONS, ps.profession, lang)}</span>}
                {ps.years_experience != null && <span className="text-[11px] text-muted-foreground">{ps.years_experience} {t("opportunities.years")}</span>}
                {ps.verification_status === "verified" && <span className="rounded-full bg-[#EBF6F0] px-2 py-0.5 text-[11px] font-medium text-[#1E6B47]">{t("status.verified")}</span>}
              </div>
            </div>
            <StatusBadge status={p.status} label={t(`status.${p.status}`)} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {(ps.city || ps.country) && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[ps.city, countryLabel(ps.country, lang)].filter(Boolean).join(", ")}</span>}
            {ps.languages?.length > 0 && <span className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{ps.languages.map((l) => languageLabel(l, lang)).join(" · ")}</span>}
            <span className="inline-flex items-center gap-1"><Wifi className="h-3.5 w-3.5" />{formatLabel}</span>
          </div>
        </div>
      </div>

      {(p.availability || p.dates || p.format_availability || p.notes) && (
        <div className="mt-3 space-y-1 rounded-xl bg-cream px-3 py-2.5 text-xs text-slateink/80">
          {p.availability && <p><strong className="font-medium">{t("initiatives.p_availability")}:</strong> {p.availability}</p>}
          {p.dates && <p><strong className="font-medium">{t("initiatives.p_dates")}:</strong> {p.dates}</p>}
          {p.format_availability && <p><strong className="font-medium">{t("initiatives.p_format")}:</strong> {p.format_availability}</p>}
          {p.notes && <p><strong className="font-medium">{t("initiatives.p_notes")}:</strong> {p.notes}</p>}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button asChild size="sm" variant="outline" data-testid={`view-profile-${p.id}`}>
          <Link to={`/profesionales/${p.psychologist_id}`} target="_blank" rel="noopener noreferrer">{t("organizer.view_profile")} <ExternalLink className="ml-1 h-3.5 w-3.5" /></Link>
        </Button>
        {showActions && (
          <>
            {p.status !== "confirmed" && <Button size="sm" onClick={() => onStatus(p.id, "confirmed")} className="bg-[#1E6B47] hover:bg-[#155235]" data-testid={`confirm-${p.id}`}>{t("common.confirm")}</Button>}
            {p.status !== "declined" && <Button size="sm" variant="outline" onClick={() => onStatus(p.id, "declined")} data-testid={`decline-${p.id}`}>{t("common.decline")}</Button>}
          </>
        )}
      </div>
    </div>
  );
}

export default function OrganizerInitiativeWorkspace() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [init, setInit] = useState(undefined);
  const [participants, setParticipants] = useState([]);
  const [editing, setEditing] = useState(false);
  const [teamFilter, setTeamFilter] = useState("all");
  const [noteText, setNoteText] = useState("");
  const [copied, setCopied] = useState(false);
  const [reach, setReach] = useState({ view: 0, share_open: 0, copy_link: 0, whatsapp: 0, email: 0, participation_request: 0 });

  const loadInit = useCallback(() => api.get(`/initiatives/${id}/manage`).then((r) => setInit(r.data)).catch(() => setInit(false)), [id]);
  const loadParts = useCallback(() => api.get(`/initiatives/${id}/participants`).then((r) => setParticipants(r.data)).catch(() => setParticipants([])), [id]);
  const loadReach = useCallback(() => api.get(`/initiatives/${id}/reach`).then((r) => setReach(r.data)).catch(() => {}), [id]);
  useEffect(() => { loadInit(); loadParts(); loadReach(); }, [loadInit, loadParts, loadReach]);

  const setPartStatus = async (pid, status) => {
    try { await api.patch(`/participants/${pid}/status`, { status }); await loadParts(); await loadInit(); toast.success(t(`status.${status}`)); }
    catch { toast.error("Error"); }
  };
  const setStatus = async (status) => {
    try { await api.patch(`/initiatives/${id}/status`, { status }); await loadInit(); } catch { toast.error("Error"); }
  };
  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    try { await api.post(`/initiatives/${id}/notes`, { text: noteText }); setNoteText(""); await loadInit(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delNote = async (noteId) => { try { await api.delete(`/initiatives/${id}/notes/${noteId}`); await loadInit(); } catch { toast.error("Error"); } };

  const copyLink = async () => {
    const url = `${window.location.origin}/iniciativas/${id}`;
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    setCopied(true); toast.success(t("organizer.link_copied")); setTimeout(() => setCopied(false), 2000);
  };

  if (init === undefined) return <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">{t("common.loading")}</div>;
  if (!init) return <div className="mx-auto max-w-3xl px-4 py-24 text-center text-muted-foreground">404</div>;

  const goal = init.participation_goal || 0;
  const pending = participants.filter((p) => p.status === "pending");
  const confirmed = participants.filter((p) => p.status === "confirmed");
  const team = teamFilter === "all" ? participants : participants.filter((p) => p.status === teamFilter);
  const publicUrl = `${window.location.origin}/iniciativas/${id}`;

  const Stat = ({ label, value, testId }) => (
    <div className="rounded-xl border border-border bg-white p-4 text-center" data-testid={testId}>
      <p className="font-display text-2xl font-bold text-slateink">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{label}</p>
    </div>
  );

  const Row = ({ label, value }) => value ? (
    <div className="flex flex-col gap-0.5 border-b border-border py-3 sm:flex-row sm:justify-between sm:gap-4">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm text-slateink/90 sm:text-right">{value}</span>
    </div>
  ) : null;

  const statusFilters = [["all", t("admin.all")], ["pending", t("status.pending")], ["confirmed", t("status.confirmed")], ["declined", t("status.declined")]];

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
      <Link to="/organizador" className="mb-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-terracotta" data-testid="back-to-dashboard">
        <ArrowLeft className="h-4 w-4" /> {t("organizer.back_dashboard")}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-2xl font-bold tracking-tight text-slateink sm:text-3xl">{init.name}</h1>
            <StatusBadge status={init.status} label={t(`status.${init.status}`)} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {init.dates && <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" />{init.dates}</span>}
            {init.location && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{init.location}</span>}
          </div>
        </div>
        <Button variant="outline" onClick={() => setEditing(true)} data-testid="workspace-edit-btn"><Pencil className="mr-1.5 h-4 w-4" />{t("organizer.quick_edit")}</Button>
      </div>

      <Tabs defaultValue="overview" className="mt-8">
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <TabsList className="inline-flex w-max">
            <TabsTrigger value="overview" data-testid="ws-tab-overview"><ClipboardList className="mr-1.5 h-4 w-4" />{t("organizer.overview")}</TabsTrigger>
            <TabsTrigger value="team" data-testid="ws-tab-team"><Users className="mr-1.5 h-4 w-4" />{t("organizer.team")}</TabsTrigger>
            <TabsTrigger value="applicants" data-testid="ws-tab-applicants"><Inbox className="mr-1.5 h-4 w-4" />{t("organizer.applicants_tab")}{pending.length > 0 && <span className="ml-1.5 rounded-full bg-terracotta px-1.5 text-[10px] text-white">{pending.length}</span>}</TabsTrigger>
            <TabsTrigger value="availability" data-testid="ws-tab-availability"><Clock className="mr-1.5 h-4 w-4" />{t("organizer.availability_tab")}</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="ws-tab-contacts"><Phone className="mr-1.5 h-4 w-4" />{t("organizer.contacts_tab")}</TabsTrigger>
            <TabsTrigger value="notes" data-testid="ws-tab-notes"><StickyNote className="mr-1.5 h-4 w-4" />{t("organizer.notes_tab")}</TabsTrigger>
            <TabsTrigger value="share" data-testid="ws-tab-share"><Share2 className="mr-1.5 h-4 w-4" />{t("organizer.share_tab")}</TabsTrigger>
            <TabsTrigger value="reach" data-testid="ws-tab-reach"><BarChart3 className="mr-1.5 h-4 w-4" />{t("organizer.reach_tab")}</TabsTrigger>
          </TabsList>
        </div>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label={t("initiatives.goal")} value={goal || "—"} testId="ws-stat-goal" />
            <Stat label={t("initiatives.applicants")} value={init.applicants} testId="ws-stat-applicants" />
            <Stat label={t("initiatives.confirmed")} value={init.confirmed} testId="ws-stat-confirmed" />
            <Stat label={t("initiatives.remaining")} value={goal ? init.remaining : "—"} testId="ws-stat-remaining" />
          </div>

          <div className="rounded-2xl border border-border bg-white p-6">
            <Row label={t("common.status")} value={t(`status.${init.status}`)} />
            <Row label={t("initiatives.dates")} value={init.dates} />
            <Row label={t("initiatives.location")} value={init.location} />
            <Row label={t("profile.service_format")} value={[init.online_required && t("common.online"), init.in_person_required && t("common.in_person")].filter(Boolean).join(" · ") || "—"} />
            <Row label={t("initiatives.languages_needed")} value={init.languages_needed?.join(" · ")} />
            <Row label={t("initiatives.requirements")} value={init.professional_requirements} />
            {init.is_opportunity && (
              <div className="mt-3 rounded-xl border border-terracotta/30 bg-cream p-4" data-testid="ws-opportunity-meta">
                <p className="text-xs font-semibold uppercase tracking-widest text-terracotta">{t("opportunities.badge")}</p>
                <div className="mt-3 space-y-0">
                  {init.required_profession_group && <Row label={t("initiatives.category")} value={professionCategoryLabel(init.required_profession_group, lang)} />}
                  {init.required_profession && <Row label={t("opportunities.required_profession")} value={localize(PROFESSIONS, init.required_profession, lang)} />}
                  {init.required_specialty && <Row label={t("initiatives.specialty")} value={specialtyLabel(init.required_specialty, lang)} />}
                  {init.required_languages?.length > 0 && <Row label={t("opportunities.required_languages")} value={init.required_languages.map((l) => languageLabel(l, lang)).join(" · ")} />}
                  {init.engagement_type && <Row label={t("opportunities.engagement_type")} value={localize(ENGAGEMENT_TYPES, init.engagement_type, lang)} />}
                  {init.compensation_type && <Row label={t("opportunities.compensation_type")} value={localize(COMPENSATION_TYPES, init.compensation_type, lang)} />}
                  {init.min_experience != null && <Row label={t("opportunities.min_experience")} value={`${init.min_experience} ${t("opportunities.years")}`} />}
                  {init.professionals_needed != null && <Row label={t("opportunities.professionals_needed")} value={String(init.professionals_needed)} />}
                  {init.application_deadline && <Row label={t("opportunities.application_deadline")} value={formatDateRange(init.application_deadline, null, lang)} />}
                  {init.required_credential_status === "verified" && <Row label={t("opportunities.verified_required")} value={t("common.yes")} />}
                </div>
              </div>
            )}
            {init.description && <div className="pt-3"><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Descripción</p><p className="mt-1 whitespace-pre-line text-sm text-slateink/80">{init.description}</p></div>}
          </div>

          <div className="rounded-2xl border border-border bg-white p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("organizer.visibility")}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {init.status !== "published" ? <Button size="sm" onClick={() => setStatus("published")} className="bg-terracotta hover:bg-terracotta-hover" data-testid="ws-publish"><Eye className="mr-1.5 h-4 w-4" />{t("organizer.publish")}</Button>
                : <Button size="sm" variant="outline" onClick={() => setStatus("draft")} data-testid="ws-unpublish"><EyeOff className="mr-1.5 h-4 w-4" />{t("organizer.unpublish")}</Button>}
              {init.status !== "closed" && <Button size="sm" variant="outline" onClick={() => setStatus("closed")} data-testid="ws-close"><XCircle className="mr-1.5 h-4 w-4" />{t("organizer.close")}</Button>}
            </div>
          </div>
        </TabsContent>

        {/* TEAM */}
        <TabsContent value="team" className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-lg font-semibold text-slateink">{t("organizer.team_roster")}</h2>
            <div className="flex flex-wrap gap-1 rounded-full border border-border bg-white p-1">
              {statusFilters.map(([v, l]) => (
                <button key={v} onClick={() => setTeamFilter(v)} data-testid={`team-filter-${v}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium ${teamFilter === v ? "bg-slateink text-white" : "text-slateink/70"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="mt-5">
            {team.length === 0 ? <EmptyState icon={Users} title={t("organizer.no_team")} testId="team-empty" />
              : <div className="space-y-3">{team.map((p) => <ParticipantCard key={p.id} p={p} t={t} showActions onStatus={setPartStatus} />)}</div>}
          </div>
        </TabsContent>

        {/* APPLICANTS */}
        <TabsContent value="applicants" className="mt-6">
          <h2 className="font-display text-lg font-semibold text-slateink">{t("organizer.new_requests")}</h2>
          <div className="mt-5">
            {pending.length === 0 ? <EmptyState icon={Inbox} title={t("organizer.no_participants")} testId="applicants-empty" />
              : <div className="space-y-3">{pending.map((p) => <ParticipantCard key={p.id} p={p} t={t} showActions onStatus={setPartStatus} />)}</div>}
          </div>
        </TabsContent>

        {/* AVAILABILITY */}
        <TabsContent value="availability" className="mt-6">
          <h2 className="font-display text-lg font-semibold text-slateink">{t("organizer.confirmed_participants")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("organizer.availability_desc")}</p>
          <div className="mt-5">
            {confirmed.length === 0 ? <EmptyState icon={Clock} title={t("organizer.no_availability")} testId="availability-empty" />
              : (
                <div className="space-y-3">
                  {confirmed.map((p) => (
                    <div key={p.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`avail-${p.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-display font-semibold text-slateink">{p.psychologist?.full_name || "—"}</p>
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Wifi className="h-3.5 w-3.5" />{p.format_availability || (p.psychologist?.service_format === "online" ? t("common.online") : p.psychologist?.service_format === "in_person" ? t("common.in_person") : t("common.both"))}</span>
                      </div>
                      <div className="mt-2 space-y-1 text-sm text-slateink/80">
                        <p><Calendar className="mr-1.5 inline h-3.5 w-3.5" />{p.dates || p.psychologist?.availability || "—"}</p>
                        {p.availability && <p className="text-xs text-muted-foreground">{p.availability}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </TabsContent>

        {/* CONTACTS */}
        <TabsContent value="contacts" className="mt-6">
          <h2 className="font-display text-lg font-semibold text-slateink">{t("organizer.confirmed_participants")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("organizer.contacts_desc")}</p>
          <div className="mt-5">
            {confirmed.length === 0 ? <EmptyState icon={Mail} title={t("organizer.no_contacts")} testId="contacts-empty" />
              : (
                <div className="space-y-3">
                  {confirmed.map((p) => (
                    <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-white p-5" data-testid={`contact-${p.id}`}>
                      <p className="font-display font-semibold text-slateink">{p.psychologist?.full_name || "—"}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slateink/80">
                        {p.psychologist?.email && <a href={`mailto:${p.psychologist.email}`} className="inline-flex items-center gap-1 hover:text-terracotta"><Mail className="h-4 w-4" />{p.psychologist.email}</a>}
                        {p.psychologist?.phone && <a href={`tel:${p.psychologist.phone}`} className="inline-flex items-center gap-1 hover:text-terracotta"><Phone className="h-4 w-4" />{p.psychologist.phone}</a>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </TabsContent>

        {/* NOTES */}
        <TabsContent value="notes" className="mt-6">
          <h2 className="font-display text-lg font-semibold text-slateink">{t("organizer.notes_title")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("organizer.notes_desc")}</p>
          <form onSubmit={addNote} className="mt-5 flex flex-col gap-3 sm:flex-row">
            <Textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder={t("organizer.note_placeholder")} data-testid="note-input" className="flex-1" />
            <Button type="submit" className="rounded-full bg-terracotta hover:bg-terracotta-hover sm:self-start" data-testid="add-note-btn"><Plus className="mr-1.5 h-4 w-4" />{t("organizer.add_note")}</Button>
          </form>
          <div className="mt-5 space-y-3">
            {(!init.notes || init.notes.length === 0) ? <p className="rounded-xl border border-dashed border-border bg-white/50 py-10 text-center text-sm text-muted-foreground" data-testid="notes-empty">{t("organizer.no_notes")}</p>
              : [...init.notes].reverse().map((n) => (
                <div key={n.id} className="flex items-start justify-between gap-3 rounded-2xl border border-border bg-white p-4" data-testid={`note-${n.id}`}>
                  <div>
                    <p className="whitespace-pre-line text-sm text-slateink/90">{n.text}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{n.author} · {new Date(n.created_at).toLocaleDateString()}</p>
                  </div>
                  <button onClick={() => delNote(n.id)} data-testid={`delete-note-${n.id}`}><Trash2 className="h-4 w-4 text-destructive" /></button>
                </div>
              ))}
          </div>
        </TabsContent>

        {/* SHARE */}
        <TabsContent value="share" className="mt-6">
          <div className="rounded-2xl border border-border bg-white p-6 sm:p-8">
            <div className="flex items-center gap-2 text-terracotta"><Share2 className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">{t("organizer.share_title")}</span></div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("organizer.share_desc")}</p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <Input readOnly value={publicUrl} className="flex-1 font-mono text-xs" data-testid="share-url-input" onFocus={(e) => e.target.select()} />
              <Button onClick={copyLink} className="rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="copy-link-btn">
                {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}{t("organizer.copy_link")}
              </Button>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("organizer.visibility")}:</span>
              <StatusBadge status={init.status} label={t(`status.${init.status}`)} />
            </div>
            {init.status !== "published" && <p className="mt-4 rounded-lg bg-cream px-3 py-2.5 text-xs text-muted-foreground" data-testid="share-draft-note">{t("organizer.share_draft_note")}</p>}
            {init.status === "published" && (
              <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-terracotta hover:underline" data-testid="open-public-link">
                {t("common.view")} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </TabsContent>

        {/* REACH */}
        <TabsContent value="reach" className="mt-6 space-y-6">
          <div>
            <h2 className="font-display text-lg font-semibold text-slateink">{t("organizer.reach_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("organizer.reach_desc")}</p>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Stat label={t("organizer.reach_views")} value={reach.view} testId="reach-views" />
            <Stat label={t("organizer.reach_shares")} value={reach.share_open} testId="reach-shares" />
            <Stat label={t("organizer.reach_copy")} value={reach.copy_link} testId="reach-copy" />
            <Stat label={t("organizer.reach_whatsapp")} value={reach.whatsapp} testId="reach-whatsapp" />
            <Stat label={t("organizer.reach_email")} value={reach.email} testId="reach-email" />
            <Stat label={t("organizer.reach_requests")} value={reach.participation_request} testId="reach-requests" />
          </div>
          <p className="rounded-lg bg-cream px-3 py-2.5 text-xs text-muted-foreground" data-testid="reach-note">{t("organizer.reach_note")}</p>
        </TabsContent>
      </Tabs>

      <InitiativeEditor open={editing} onOpenChange={setEditing} initiative={init} onSaved={() => loadInit()} />
    </div>
  );
}
